import React from 'react';

import { ApolloError, gql, QueryResult } from '@apollo/client';
import { useLocation, useSearchParams } from '@remix-run/react';

import { OdProtosSellCommon_Product } from '__generated__/globalTypes';

import { CUSTOMER_UUID_QUERY_PARAM } from 'shared/helpers/globals';
import { useTrackGenericErrorPageViewEvent } from 'shared/routes/errors/GenericErrorPage';

import { useAuthenticatedQuery } from 'platform/apollo';
import { useAuthAuthenticated } from 'platform/auth-fe';
import {
  ListLeadsByCustomer,
  ListLeadsByCustomer_seller_listLeadsOnUniqueAddresses_leads,
  ListLeadsByCustomer_seller_listLeadsOnUniqueAddresses_leads_address,
  ListLeadsByCustomerVariables,
} from 'platform/graphql/__generated__/ListLeadsByCustomer';
import { useObservability } from 'platform/observability';
import { parseAddressRoute } from 'platform/routingHelpers';

/**
 * Fetches all leads associated with a customer (from authenticated user or override)
 */
export const ListLeadsByCustomerQuery = gql`
  query ListLeadsByCustomer($customerUUID: String!) {
    seller {
      listLeadsOnUniqueAddresses(input: { customerUuid: $customerUUID }) {
        leads {
          id
          address {
            street
            uuid
            unitNumber
          }
          productOfferings {
            product
            deniedAt
            denialReason
          }
        }
      }
    }
  }
`;

/**
 * Addresses can be null on the seller lead, this guarantees it's existence
 */
export type CustomerLeadWithGuaranteedAddress = Omit<
  ListLeadsByCustomer_seller_listLeadsOnUniqueAddresses_leads,
  'address'
> & {
  address: ListLeadsByCustomer_seller_listLeadsOnUniqueAddresses_leads_address;
};

export type CustomerAddressUUIDResponse = {
  customerUUID: string;
  addressUUID: string | undefined;
  customerLeads: CustomerLeadWithGuaranteedAddress[];
  loading: boolean;
  error: ApolloError | undefined;
  refetch: QueryResult<ListLeadsByCustomer, ListLeadsByCustomerVariables>['refetch'];
};

/**
 * Returns the calculated customer UUID, address UUID for the current lead
 * @throws Error if no customer is found
 */
export const useCustomerAddressUUID = (): CustomerAddressUUIDResponse => {
  const { trackError } = useObservability();
  const { trackGenericErrorPageView } = useTrackGenericErrorPageViewEvent();
  const authentication = useAuthAuthenticated();
  const { pathname } = useLocation();
  const { addressUUID: addressUUIDParam } = parseAddressRoute(pathname);
  const [params] = useSearchParams();

  // Only OD internal users can override the customer UUID
  const customerUUIDOverride = authentication.user.hasOpendoorInternalAccess
    ? params.get(CUSTOMER_UUID_QUERY_PARAM)
    : undefined;

  /**
   * Compute source of truth for customer UUID
   */
  const customerUUID = React.useMemo(
    () =>
      // query param to override authenticated user√
      customerUUIDOverride ||
      // authenticated customer UUID
      authentication?.user?.customerUuid,
    [customerUUIDOverride, authentication?.user?.customerUuid],
  );

  /**
   * Customer UUID is required for all dashboard routes.
   */
  if (!customerUUID || customerUUID.trim().length === 0) {
    trackGenericErrorPageView('missing-customer-uuid', 'system-error');
    throw new Error('Missing customer UUID');
  }

  /**
   * Fetches all leads associated with a customer (from authenticated user or override)
   */
  const sellerLeadFromCustomerQuery = useAuthenticatedQuery<
    ListLeadsByCustomer,
    ListLeadsByCustomerVariables
  >(ListLeadsByCustomerQuery, {
    variables: { customerUUID: customerUUID as string },
    fetchPolicy: 'cache-first',
    skip: !customerUUID,
    context: { linkContext: { team: 'seller-engineering' } },
  });

  // Filter out leads that don't have an address uuid
  const customerLeads: CustomerLeadWithGuaranteedAddress[] = React.useMemo(() => {
    const leadsWithAddressUUIDs: CustomerLeadWithGuaranteedAddress[] = [];
    sellerLeadFromCustomerQuery.data?.seller?.listLeadsOnUniqueAddresses?.leads.forEach((lead) => {
      if (lead.address?.uuid) {
        leadsWithAddressUUIDs.push(lead as CustomerLeadWithGuaranteedAddress);
      } else {
        trackError('lead-missing-address-uuid', undefined, { sellerLeadUUID: lead.id });
      }
    });

    return leadsWithAddressUUIDs.filter((l) =>
      l.productOfferings.some(
        (po) => po.product === OdProtosSellCommon_Product.OPENDOOR_DIRECT && po.deniedAt === null,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute on explicit dependencies
  }, [sellerLeadFromCustomerQuery.data?.seller?.listLeadsOnUniqueAddresses?.leads]);

  const latestCustomerAddressUUID =
    customerLeads.length > 0 ? customerLeads[customerLeads.length - 1].address?.uuid : undefined;

  /**
   * Compute source of truth for address UUID
   */
  const addressUUID = React.useMemo(
    () =>
      addressUUIDParam ||
      // fetched from latest customer lead
      latestCustomerAddressUUID,
    [addressUUIDParam, latestCustomerAddressUUID],
  );

  return {
    customerUUID,
    addressUUID,
    customerLeads,
    loading: sellerLeadFromCustomerQuery.loading,
    error: sellerLeadFromCustomerQuery.error,
    refetch: sellerLeadFromCustomerQuery.refetch,
  };
};
